import { useQueryClient } from '@tanstack/react-query'
import { CarouselQueryKeys } from '@/modules/client-setting/keys'
import useQueryGetCarousel from '@/modules/client-setting/api/hooks/useQueryGetCarousel'
import useMutationCreateCarouselItem from '@/modules/client-setting/api/hooks/useMutationCreateCarouselItem'
import useMutationDeleteCarouselItem from '@/modules/client-setting/api/hooks/useMutationDeleteCarouselItem'
import useMutationUpdateCarouselItem from '../../api/hooks/useMutationUpdateCarouselItem'
import useMutationCreateCarouselFileUrl from '@/modules/operator/api/hooks/useMutationCreateCarouselFileUrl'
import useQueryGetGeneralSetting from '@/modules/client-setting/api/hooks/useQueryGetGeneralSetting'
import ImageUploadForm from '../../../../common/components/ImageUploadForm'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { CarouselItemSize } from '@/schema'

export default function CarouselForm() {
	const queryClient = useQueryClient()
	const { data: carousel, isLoading } = useQueryGetCarousel()
	const { data: generalSettings } = useQueryGetGeneralSetting()
	const { mutateAsync: createCarouselItem } = useMutationCreateCarouselItem()
	const { mutate: updateCarouselItem } = useMutationUpdateCarouselItem()
	const { mutate: deleteCarouselItem } = useMutationDeleteCarouselItem()
	const { mutateAsync: getCarouselFileUrl } = useMutationCreateCarouselFileUrl()

	const handleUpdate = (params: { id: string; payload: any }) => {
		updateCarouselItem(params, {
			onSuccess: () => {
				toast.success('แก้ไขสำเร็จ')
				queryClient.invalidateQueries({
					queryKey: [CarouselQueryKeys.GetCarousel],
				})
			},
			onError: (error) => {
				showErrorToast(error)
			},
		})
	}

	const handleDelete = (params: { id: string; itemId: string }) => {
		deleteCarouselItem(params, {
			onSuccess: () => {
				toast.success('ลบรูปสไลด์สำเร็จ')
				queryClient.invalidateQueries({
					queryKey: [CarouselQueryKeys.GetCarousel],
				})
			},
			onError: (error) => {
				showErrorToast(error)
			},
		})
	}

	// Determine aspect ratio based on carousel item size
	const getAspectRatio = () => {
		const size = generalSettings?.carouselItemSize ?? CarouselItemSize.LARGE
		switch (size) {
			case CarouselItemSize.SMALL:
				return 16 / 7
			case CarouselItemSize.MEDIUM:
				return 16 / 5
			case CarouselItemSize.LARGE:
			default:
				return 16 / 9
		}
	}

	const getAspectRatioLabel = () => {
		const size = generalSettings?.carouselItemSize ?? CarouselItemSize.LARGE
		switch (size) {
			case CarouselItemSize.SMALL:
				return '16:7'
			case CarouselItemSize.MEDIUM:
				return '16:5'
			case CarouselItemSize.LARGE:
			default:
				return '16:9'
		}
	}

	const aspectRatio = getAspectRatio()
	const aspectRatioLabel = getAspectRatioLabel()
	// Calculate height based on a fixed width of 416px (Tailwind w-full with typical container)
	const calculatedHeight = Math.round(416 / aspectRatio)

	return (
		<ImageUploadForm
			title="รูปสไลด์"
			uploadButtonText={`อัพโหลดรูป (สัดส่วน ${aspectRatioLabel})`}
			uploadButtonHeight={`${calculatedHeight}px`}
			cropperHeight={`${calculatedHeight}px`}
			cropperAspect={aspectRatio}
			items={carousel?.data}
			isLoading={isLoading}
			queryKey={[CarouselQueryKeys.GetCarousel]}
			createItem={createCarouselItem}
			updateItem={handleUpdate}
			deleteItem={handleDelete}
			getUploadFileUrl={getCarouselFileUrl}
		/>
	)
}
